<template>
	<el-container class="layout-container">
		<el-header class="nav-container">
			<navbar 
				:navItems="navItems" 
				:main-route="mainRoute" 
				@reload="reload"
				@toggle-mobile-sidebar="toggleMobileSidebar"
			>
				<slot name="header" />
			</navbar>
		</el-header>
		<el-container class="main-container">
			<el-aside width="auto" class="aside-container" :class="{ 'mobile-open': mobileSidebarOpen }">
				<sidebar 
					:side-items="sideItems" 
					:main-route="mainRoute" 
					@toggle-collapse="handleSidebarToggle"
					ref="sidebar"
				>
					<slot name="default" />
				</sidebar>
				<!-- 移动端遮罩层 -->
				<div v-if="isMobile && mobileSidebarOpen" class="mobile-mask" @click="closeMobileSidebar"></div>
			</el-aside>
			<el-main class="content-main" :class="{ 'mobile-sidebar-open': mobileSidebarOpen }">
				<!-- 使用封装的标签页组件 -->
				<tabpage 
					:current-route="$route"
					@refresh="reload"
					ref="pageTabs"
				/>
				
				<router-view v-if="isAlive" class="router-view" />
			</el-main>
		</el-container>
	</el-container>
</template>

<script>
	import navbar from './navbar';
	import sidebar from './sidebar';
	import tabpage from './tabpage.vue'; // 导入标签页组件

	export default {
		name: 'Layout',
		components: {
			navbar,
			sidebar,
			tabpage
		},
		props: {
			mainRoute: {
				type: String,
				default: "/"
			},
			navItems: {
				type: Array,
				default: () => []
			},
			sideItems: {
				type: Array,
				default: () => []
			}
		},
		data() {
			return {
				isAlive: true,
				mobileSidebarOpen: false,
				isMobile: false,
				windowWidth: 0
			}
		},
		mounted() {
			this.checkDeviceType();
			window.addEventListener('resize', this.handleResize);
			// 初始化标签页（如果需要的话）
			if (this.$refs.pageTabs) {
				this.$refs.pageTabs.initVisitedRoutes(this.$route);
			}
		},
		beforeDestroy() {
			window.removeEventListener('resize', this.handleResize);
		},
		methods: {
			reload() {
				this.isAlive = false;
				this.$nextTick(() => {
					this.isAlive = true;
				});
			},
			handleSidebarToggle(collapsed) {
				if (this.isMobile) {
					this.mobileSidebarOpen = !collapsed;
				}
			},
			toggleMobileSidebar() {
				if (this.isMobile) {
					this.mobileSidebarOpen = !this.mobileSidebarOpen;
					// 同步更新 sidebar 组件的状态
					if (this.$refs.sidebar) {
						if (this.mobileSidebarOpen) {
							this.$refs.sidebar.openMobileSidebar();
						} else {
							this.$refs.sidebar.closeMobileSidebar();
						}
					}
				}
			},
			closeMobileSidebar() {
				this.mobileSidebarOpen = false;
				if (this.$refs.sidebar) {
					this.$refs.sidebar.closeMobileSidebar();
				}
			},
			handleResize() {
				this.windowWidth = window.innerWidth;
				this.checkDeviceType();
				
				// 在桌面端自动关闭移动端侧边栏状态
				if (!this.isMobile) {
					this.mobileSidebarOpen = false;
					if (this.$refs.sidebar) {
						this.$refs.sidebar.closeMobileSidebar();
					}
				}
			},
			checkDeviceType() {
				const width = window.innerWidth;
				this.isMobile = width <= 768;
				
				// 移动端默认关闭侧边栏
				if (this.isMobile) {
					this.mobileSidebarOpen = false;
				}
			}
		}
	};
</script>

<style lang="scss" scoped>
	.layout-container {
		height: 100vh;
		width: 100%;
		background: #f5f7fa;
		overflow: hidden;

		// 移动端移除最小宽度限制
		@media (min-width: 769px) {
			min-width: 1024px;
		}
	}

	.nav-container {
		padding: 0;
		z-index: 1000;
		height: 64px !important;
		position: relative;
		flex-shrink: 0;

		// 移动端适配
		@media (max-width: 768px) {
			height: 56px !important;
			min-width: auto;
		}
	}

	.main-container {
		height: calc(100vh - 64px);
		overflow: hidden;
		position: relative;
		display: flex;
		flex: 1;

		@media (max-width: 768px) {
			height: calc(100vh - 56px);
		}
	}

	.aside-container {
		position: relative;
		background: #2d3037;
		box-sizing: border-box;
		border-right: 1px solid #e6e6e6;
		transition: all 0.3s ease;
		height: 100%;
		overflow: visible;
		flex-shrink: 0;
		z-index: 999;

		// 侧边栏阴影效果
		&::after {
			content: '';
			position: absolute;
			top: 0;
			right: -4px;
			bottom: 0;
			width: 4px;
			background: linear-gradient(90deg, rgba(0, 0, 0, 0.1) 0%, transparent 100%);
			pointer-events: none;
			
			@media (max-width: 768px) {
				display: none;
			}
		}

		// 移动端样式
		@media (max-width: 768px) {
			position: fixed;
			top: 56px;
			left: 0;
			bottom: 0;
			transform: translateX(-100%);
			transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
			z-index: 998;
			box-shadow: 2px 0 12px rgba(0, 0, 0, 0.15);

			&.mobile-open {
				transform: translateX(0);
			}
		}
	}

	// 移动端遮罩层
	.mobile-mask {
		position: fixed;
		top: 56px;
		left: 0;
		right: 0;
		bottom: 0;
		background: rgba(0, 0, 0, 0.5);
		z-index: 997;
		backdrop-filter: blur(2px);
		
		@media (min-width: 769px) {
			display: none;
		}
	}

	.content-main {
		padding: 0;
		background: #f5f7fa;
		position: relative;
		flex: 1;
		transition: all 0.3s ease;
		display: flex;
		flex-direction: column;

		// 主要内容区域滚动优化
		::v-deep .el-main__content {
			height: 100%;
			overflow: auto;
		}
	}

	.router-view {
		flex: 1;
		width: 100%;
		position: relative;
		overflow: auto;
		background: #f5f7fa;
		
		// 没有标签时的全屏高度
		&:only-child {
			height: 100%;
		}

		// 页面切换动画
		&.router-enter-active,
		&.router-leave-active {
			transition: all 0.3s ease;
		}

		&.router-enter-from {
			opacity: 0;
			transform: translateX(20px);
		}

		&.router-leave-to {
			opacity: 0;
			transform: translateX(-20px);
		}
	}

	// 响应式设计
	@media (max-width: 768px) {
		.layout-container {
			min-width: 100%;
		}

		// 移动端横屏优化
		@media (max-height: 500px) and (orientation: landscape) {
			.main-container {
				height: calc(100vh - 48px);
			}
			
			.nav-container {
				height: 48px !important;
			}
			
			.aside-container {
				top: 48px;
			}
			
			.mobile-mask {
				top: 48px;
			}
			
			.page-tabs-container {
				height: 36px;
			}
		}
	}
	
	/* 修复双重滚动条问题 */
	.content-main {
	  display: flex;
	  flex-direction: column;
	  height: 100%;
	}
	
	.router-view {
	  flex: 1;
	  min-height: 0;  /* 重要：允许flex子项收缩 */
	  overflow: hidden;  /* 隐藏外层滚动 */
	  position: relative;
	}
	
	/* 确保每个页面的根容器正确处理滚动 */
	.router-view > * {
	  height: 100%;
	  overflow: hidden;
	  position: relative;
	}
</style>